import { useState, type ReactNode } from 'react';
import type { BuildManualStep, BuildSetupSteps, IssueDraft } from '../api';

// The by-hand side of the GitHub actions (P5-17). The NeuralRepo app only reads
// repositories (D-121), so opening an issue, adding the build workflow and
// starting a build happen on GitHub, or from Claude Code with the user's own
// `gh`. NeuralRepo still sees each one through its webhooks.

export function CopyCommand({ command, label = 'Copy command' }: { command: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the command stays visible to select by hand.
    }
  };
  return (
    <div className="gh-command">
      <code className="gh-command-text" title={command}>{command}</code>
      <button type="button" className="btn btn-ghost-sm" onClick={() => void copy()}>{copied ? 'Copied' : label}</button>
    </div>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return <a className="btn btn-secondary btn-sm" href={href} target="_blank" rel="noopener noreferrer">{children} ↗</a>;
}

/** Open issue, by hand: GitHub's pre-filled page, or `gh issue create`. */
export function ManualIssue({ draft }: { draft: IssueDraft }) {
  return (
    <div className="gh-manual" role="status">
      <p>NeuralRepo can read <span className="mono">{draft.repo}</span> but not open issues there. Submit the pre-filled issue on GitHub; it links back to this idea by itself.</p>
      <div className="gh-manual-actions"><External href={draft.url}>Open on GitHub</External></div>
      <p className="gh-manual-alt">Or in Claude Code or a terminal:</p>
      <CopyCommand command={draft.gh_command} />
    </div>
  );
}

/** Build with Claude, by hand: label the issue, or open the pre-filled one that carries the label. */
export function ManualBuild({ step }: { step: BuildManualStep }) {
  return (
    <div className="gh-manual" role="status">
      {step.issue_url ? (
        <>
          <p>Add the <span className="mono">{step.label}</span> label to the idea's issue on GitHub to start the build. It shows here once it starts.</p>
          <div className="gh-manual-actions"><External href={step.issue_url}>Open the issue</External></div>
        </>
      ) : (
        <>
          <p>Submit the pre-filled issue on GitHub. It carries the <span className="mono">{step.label}</span> label, so the build starts, and it links back to this idea.</p>
          {step.new_issue && <div className="gh-manual-actions"><External href={step.new_issue.url}>Open on GitHub</External></div>}
        </>
      )}
      <p className="gh-manual-alt">Or in Claude Code or a terminal:</p>
      <CopyCommand command={step.gh_command} />
    </div>
  );
}

/** Turn builds on, by hand: one file and one label, then check again. */
export function ManualSetup({ setup, onCheck, checking }: { setup: BuildSetupSteps; onCheck: () => void; checking: boolean }) {
  return (
    <div className="gh-manual" role="status">
      <p>NeuralRepo can read this repository but not add files to it. Three steps, once:</p>
      <ol className="gh-manual-steps">
        <li>
          Add the workflow as <span className="mono">{setup.path}</span> and commit it.{' '}
          {setup.new_file_url && <a href={setup.new_file_url} target="_blank" rel="noopener noreferrer">Create it on GitHub ↗</a>}
        </li>
        <li>
          Create the <span className="mono">{setup.label}</span> label.{' '}
          {setup.labels_url && <a href={setup.labels_url} target="_blank" rel="noopener noreferrer">Labels ↗</a>}
          {setup.gh_commands[0] && <CopyCommand command={setup.gh_commands[0]} />}
        </li>
        <li>
          Run <span className="mono">/install-github-app</span> in Claude Code. It installs the Claude GitHub app and adds your Anthropic key as a repository secret.
        </li>
      </ol>
      <p className="gh-manual-alt">In Claude Code you can also ask Claude to set up NeuralRepo Claude builds: it writes the file and the label for you.</p>
      <div className="gh-manual-actions">
        <button type="button" className="btn btn-primary btn-sm" disabled={checking} onClick={onCheck}>{checking ? 'Checking…' : 'Check again'}</button>
      </div>
    </div>
  );
}
